import { useCallback } from "react";
import { useProductOperations } from "./ProductOperations/useProductOperations";
import { useCasePackOperations } from "./CasePackOperations/useCasePackOperations";
import { usePackageCreation } from "./PackageOperations/usePackageCreation";
import { BulkUploadData } from "./types";
import { log } from "@deliverr/commons-logs";
import { BATCH_PROCESSING, chunkArray } from "common/constants/processing.const";

export const useIntlDtcSingleSkuBulkUploadHandler = () => {
  const { removeExistingProducts, addNewProducts } = useProductOperations();
  const { fetchProductCasePacksForMultipleProducts, processCasePacksForMultipleProducts } = useCasePackOperations();
  const { createBulkPackagesFromBulkUpload, registerBulkPackages, createCaseDimensionsFromBulkUpload } =
    usePackageCreation();

  const handleIntlDtcSingleSkuBulkUploadSuccess = useCallback(
    async ({ products, caseDimensions = [], lotTracking, packages }: BulkUploadData) => {
      try {
        if (!products.length) {
          return;
        }

        // Pre-filter valid products once to avoid repeated filtering
        const validProducts = products.filter((product) => product?.dsku);
        if (!validProducts.length) {
          return;
        }

        log.info(`Starting bulk upload for ${validProducts.length} products`);

        // Extract DSKUs once for reuse
        const dskus = validProducts.map((product) => product.dsku);

        // Phase 1: Clear existing state and add new products in chunks for better performance
        try {
          removeExistingProducts();

          // Add products in chunks to avoid blocking UI
          const productChunks = chunkArray(validProducts, BATCH_PROCESSING.PROCESSING_CHUNK_SIZE);
          for (const chunk of productChunks) {
            addNewProducts(chunk);
            // Small delay to prevent UI blocking
            await new Promise((resolve) => setTimeout(resolve, 10));
          }
        } catch (error) {
          log.error("Failed to manage products:", error);
          throw error;
        }

        // Phase 2: Maximum parallel execution of independent operations
        let allProductCasePacks: Record<string, any[]> = {};
        try {
          const [casePacksResult] = await Promise.all([
            // API call for case packs (chunked internally)
            fetchProductCasePacksForMultipleProducts(dskus),
            // Case dimensions processing (independent)
            Promise.resolve().then(() => {
              try {
                createCaseDimensionsFromBulkUpload(caseDimensions);
              } catch (error) {
                log.error("Failed to process case dimensions:", error);
              }
            }),
          ]);
          allProductCasePacks = casePacksResult;

          log.info(`Fetched case packs for ${Object.keys(allProductCasePacks).length} products`);
        } catch (error) {
          log.error("Failed to fetch case pack data:", error);
          // Continue with empty case packs rather than failing completely
          allProductCasePacks = {};
        }

        // Phase 3: Optimize case pack request preparation
        const casePackRequests = validProducts.map((product) => ({
          product,
          caseDimensions: caseDimensions.filter((cd) => cd.dsku === product.dsku),
          existingCasePacks: allProductCasePacks[product.dsku] || [],
        }));

        // Phase 4: Case pack processing with improved error handling
        let processedCasePacksMap: Record<string, any[]> = {};
        try {
          processedCasePacksMap = await processCasePacksForMultipleProducts(casePackRequests);
        } catch (error) {
          log.error("Critical failure in case pack processing:", error);
          return;
        }

        // Phase 5: Parallel package creation and registration
        try {
          const finalPackageData = createBulkPackagesFromBulkUpload(
            validProducts,
            processedCasePacksMap,
            caseDimensions,
            packages
          );

          if (!finalPackageData || Object.keys(finalPackageData).length === 0) {
            throw new Error("No packages were created");
          }

          registerBulkPackages(finalPackageData);
        } catch (error) {
          log.error("Failed to create or register packages:", error);
          return;
        }
      } catch (error) {
        log.error("Unexpected error during bulk upload:", error);
      }
    },
    [
      removeExistingProducts,
      addNewProducts,
      fetchProductCasePacksForMultipleProducts,
      processCasePacksForMultipleProducts,
      createBulkPackagesFromBulkUpload,
      registerBulkPackages,
      createCaseDimensionsFromBulkUpload,
    ]
  );

  return { handleIntlDtcSingleSkuBulkUploadSuccess };
};
